import { lazy, useCallback, useEffect, memo, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'

import Bugsnag from '@bugsnag/js'

import { observer } from 'mobx-react'

import AbortDetector from 'AbortDetector'
import Heartbeat from 'Heartbeat'
import IdleDetector from 'containers/IdleDetector'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { startApp } from 'startup'
import { useUrlParams } from 'stores/utils/hooks/useUrlParams'
import { useStores } from 'stores/utils/hooks/useStores'
import {
  setGlobalEventProperty,
  trackInAmplitude
} from 'utils/tracking/amplitude'
import {
  AmplitudeEvent,
  EventProperty
} from 'utils/tracking/amplitude/amplitude.interface'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { ErrorProvider } from 'context/Error'
import { UserProvider } from 'context/User'
import { StylesProvider } from '@mui/styles'
import { StyledEngineProvider } from '@mui/material/styles'
import { generateClassName } from 'theme/material'
import Routes from './Routes'
import LoadingProvider, { LoadingMode } from './components/LoadingProvider'
import { isDevMenuEnabled } from './utils/env'
import ProgressProvider from './ProgressContext'
import ThemeProvider from './ThemeProvider'
import getExperimentVariation from 'utils/tracking/optimizely/getExperimentVariation'
import { Experiments } from 'CustomOptimizelyProvider'
import { VoucherBanner } from 'components/VoucherBanner'
import { CONFIG_DDF_SMAVA_RCE_EDIT } from 'config/utils/config'
import { getRceCustomerCategory } from 'utils/getRceCustomerCategory'
import { getRCEVoucherExperiment } from 'components/VoucherBanner/helpers/voucherExperiment'

/**
 * We load the container lazily here
 * because we don't want it to be loaded in the production bundle
 */
const DevContainer = isDevMenuEnabled()
  ? lazy(() => import('./devTools/DevContainer'))
  : () => null

const Metadata = observer(() => {
  const { traversal } = useStores()
  const {
    traversalId,
    data: {
      system: { advertisementId }
    }
  } = traversal

  useEffect(() => {
    setGlobalEventProperty(EventProperty.TraversalId, traversalId)

    Bugsnag.addMetadata('traversal', {
      id: traversalId,
      advertisementId
    })
  }, [traversalId, advertisementId])

  return null
})

const App = () => {
  const {
    rootStore: {
      resumeDetailsFetched,
      page: {
        config: { name: configName }
      },
      navigation: { parentUrl }
    },
    rootStore
  } = useRootStore()

  const urlParams = useUrlParams()
  const navigate = useNavigate()

  const boot = useCallback(() => {
    rootStore.setLoadingMode(LoadingMode.Initial)

    try {
      trackInAmplitude(AmplitudeEvent.OnTaurineStartsLoading, {
        timestamp: Number(new Date())
      })
      startApp(rootStore, navigate, urlParams)
    } catch (error) {
      if (error instanceof Error) {
        logAndTrackError(error)
      }
    } finally {
      rootStore.setLoadingMode(LoadingMode.None)
    }
  }, [rootStore, urlParams, navigate])

  useEffect(() => {
    boot()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // eslint-disable react-hooks/exhaustive-deps
    if (!parentUrl) {
      return
    }

    getExperimentVariation(Experiments.CAT_4359_DAC_SKIP_BUTTON_V3, rootStore, {
      utm_campaign: urlParams.utm_source || '',
      taurine_parent_url: parentUrl || ''
    })

    // KILL SWITCHES
    getExperimentVariation(Experiments.CAT_3494_DAC_KILL_SWITCH, rootStore, {
      utm_source: urlParams.utm_source || ''
    })

    getExperimentVariation(
      Experiments.CAT_3614_CAPTCHA_KILL_SWITCH,
      rootStore,
      {
        utm_source: urlParams.utm_source || ''
      }
    )

    getExperimentVariation(Experiments.CAT_4478_DAC_SKIP_BUTTON_V4, rootStore, {
      utm_source: urlParams.utm_source || '',
      taurine_parent_url: parentUrl || ''
    })

    getExperimentVariation(Experiments.CAT_4521_FFG_AB_VOUCHER, rootStore, {
      utm_source: urlParams.utm_source || '',
      taurine_parent_url: parentUrl || ''
    })

    getExperimentVariation(
      Experiments.CAT_4707_SMAVA_VOUCHER_HIGHER_AMOUNTS,
      rootStore,
      {
        utm_source: urlParams.utm_source || '',
        taurine_parent_url: parentUrl || ''
      }
    )

    if (configName === CONFIG_DDF_SMAVA_RCE_EDIT) {
      const rceVoucherExperiment = getRCEVoucherExperiment(
        getRceCustomerCategory({
          utm_campaign: urlParams.utm_campaign,
          parentUrl
        })
      )
      if (
        rceVoucherExperiment &&
        !rootStore.experimentData[rceVoucherExperiment]
      ) {
        getExperimentVariation(rceVoucherExperiment, rootStore, {
          utm_campaign: urlParams.utm_campaign || '',
          taurine_parent_url: parentUrl || ''
        })
      }
    }
  }, [
    resumeDetailsFetched,
    parentUrl,
    configName,
    rootStore,
    urlParams.utm_campaign,
    urlParams.utm_source
  ])

  /*
    This is useEffect for calling an Amplitude event: OnTaurineBecomesUsable.
    If `.MuiSkeleton-pulse` exists in DOM/body that means inputs are still loading.
    When they are all gone, it means Taurine if fully interactive.
   */
  useEffect(() => {
    const callback = (
      mutationsList: MutationRecord[],
      bodyObserver: MutationObserver
    ) => {
      for (const mutationList of mutationsList) {
        if (mutationList.removedNodes.length > 0) {
          const skeletonPulse = document.querySelector('.MuiSkeleton-pulse')

          if (!skeletonPulse) {
            trackInAmplitude(AmplitudeEvent.OnTaurineBecomesUsable, {
              timestamp: Number(new Date())
            })
            bodyObserver.disconnect()
            break
          }
        }
      }
    }

    const bodyObserver = new MutationObserver(callback)
    const body = document.querySelector('body')
    const config = { childList: true, subtree: true }

    if (body) {
      bodyObserver.observe(body, config)
    }

    return () => {
      bodyObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider>
        <StylesProvider generateClassName={generateClassName}>
          <ProgressProvider>
            <UserProvider>
              <ErrorProvider>
                <LoadingProvider>
                  <VoucherBanner />
                  <Routes navigate={navigate} />
                </LoadingProvider>
              </ErrorProvider>
              <IdleDetector />
            </UserProvider>
            <Metadata />
            <Heartbeat />
            <AbortDetector />
            <Suspense fallback={null}>
              <DevContainer />
            </Suspense>
          </ProgressProvider>
        </StylesProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default memo(observer(App))
